import { Alert, Button, Card, Input, Modal, Space, Typography } from "antd";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { Grid as ZhGrid, GridItem as ZhGridItem } from "zhihao-ui-react";

import request from "@/api";
import { DEFAULT_VALUE } from "@/config";
import { useUserStore } from "@/views/stores";
import {
    ANNUAL_MEMBER_STATE,
    FREE_NUM_UNIT,
    GIFT_DURATION_UNIT,
    GIFT_MEMBER_STATE,
    VISIBLE_CONTENT_STATE
} from "@zhihaoscm/meta";

export interface MemberLevelRecord {
    id: number;
    name?: string;
    state?: number | string;
    yearFee?: number;
    yearDiscountFee?: number;
    yearDiscountFlag?: number;
    giftFlag?: number;
    giftDuration?: number;
    giftDurationUnit?: number;
    memberPermission?: Record<string, unknown>;
    feature?: Record<string, unknown>;
}

export interface MemberLevelPageProps {
    loadLevels?: () => Promise<MemberLevelRecord[]>;
    updateState?: (id: number, state: number) => Promise<unknown>;
    loadRegisterConfig?: (type: number) => Promise<RegisterGiveConfig>;
    saveRegisterConfig?: (id: number, params: RegisterGiveConfigPayload) => Promise<unknown>;
}

interface RegisterGiveContent {
    hasOpen?: number;
    level?: number;
    duration?: number;
    effectiveDuration?: number;
}

interface RegisterGiveConfig {
    id?: number;
    content?: string;
}

interface RegisterGiveConfigPayload {
    type: number;
    content: string;
}

export interface MemberLevelFormPageProps {
    memberId?: number;
    loadDetail?: (id: number) => Promise<MemberLevelRecord>;
    updateMember?: (record: MemberLevelRecord) => Promise<unknown>;
}

/** 会员等级状态沿用 Vue MEMBER_OPEN_STATE：1 开放，0 关闭。 */
const isOpen = (record: MemberLevelRecord) => Number(record.state) === 1;

/** 会员费用沿用 Vue yearDiscountFee || yearFee 的展示优先级。 */
const formatFee = (record: MemberLevelRecord) => {
    const fee = record.yearDiscountFee || record.yearFee;
    return fee == null ? DEFAULT_VALUE : `¥${Number(fee).toFixed(2)}/年`;
};

/** 复用 Vue `/admin/membership-level/all` 等级列表接口。 */
export async function loadMemberLevels(): Promise<MemberLevelRecord[]> {
    const response = await request.service.get("/admin/membership-level/all");
    return Array.isArray(response.data) ? response.data : [];
}

/** 复用 Vue `/admin/membership-level/:id/state/:state` 状态接口。 */
export async function updateMemberLevelState(id: number, state: number): Promise<unknown> {
    const response = await request.service.put(`/admin/membership-level/${id}/state/${state}`);
    return response.data;
}

/** 复用旧 Vue 注册送会员配置读取接口，业务设置类型固定为 2。 */
export async function loadRegisterGiveConfig(type: number): Promise<RegisterGiveConfig> {
    const response = await request.service.get(`/admin/business-config/type?type=${type}`);
    return response.data;
}

/** 复用旧 Vue 注册送会员配置保存接口，保持 type/content JSON 契约。 */
export async function saveRegisterGiveConfig(id: number, params: RegisterGiveConfigPayload): Promise<unknown> {
    const response = await request.service.put(`/admin/business-config/${id}`, params);
    return response.data;
}

const parseRegisterGiveContent = (content?: string): RegisterGiveContent => {
    if (!content) return {};
    try {
        return JSON.parse(content) as RegisterGiveContent;
    } catch {
        return {};
    }
};

/** 复用 Vue `/admin/membership-level/vo/:id` 详情接口。 */
export async function loadMemberLevelDetail(id: number): Promise<MemberLevelRecord> {
    const response = await request.service.get(`/admin/membership-level/vo/${id}`);
    return response.data;
}

/** 复用 Vue `/admin/membership-level` 会员等级更新接口。 */
export async function updateMemberLevel(record: MemberLevelRecord): Promise<unknown> {
    const response = await request.service.put("/admin/membership-level", record);
    return response.data;
}

interface MemberPermissionOption {
    key: string;
    label: string;
    layout: number;
    props: Record<string, unknown>;
}

/** 创建旧 Vue 会员权限配置的默认模型，避免把配置退化成不可编辑的 JSON 文本。 */
function createPermissionOptions(): MemberPermissionOption[] {
    return [
        {
            key: "productTypeIndexCheck",
            label: "砂石指数价格查看",
            layout: 1,
            props: { hasUnLimit: 1, visibleRange: [1] }
        },
        {
            key: "shippingPriceIndexCheck",
            label: "运价指数价格查看",
            layout: 1,
            props: { hasUnLimit: 1, visibleRange: [1] }
        },
        { key: "shipQuery", label: "查船舶", layout: 2, props: { hasUnLimit: 1, times: undefined } },
        { key: "carQuery", label: "查车辆", layout: 2, props: { hasUnLimit: 1, times: undefined } },
        { key: "memberInformation", label: "会员专享资讯", layout: 3, props: { hasUnLimit: 1 } },
        {
            key: "intelligentAssistant",
            label: "智能助手功能",
            layout: 2,
            props: { hasUnLimit: 1, times: undefined }
        },
        { key: "manualService", label: "专属客服1V1", layout: 4, props: { hasSupport: 0 } },
        {
            key: "subAccountNum",
            label: "企业子账号",
            layout: 5,
            props: { hasSupport: 0, supportNum: undefined }
        },
        { key: "offlineBooth", label: "线下活动展位", layout: 6, props: { hasSupport: 0 } },
        { key: "mediaPromotion", label: "自媒体推广", layout: 7, props: { hasSupport: 0, freeNumUnit: 2 } }
    ];
}

/** 将旧接口 feature/memberPermission 回显为可编辑的权限行。 */
function hydratePermissionOptions(permission: Record<string, unknown> | undefined): MemberPermissionOption[] {
    const source = permission || {};
    return createPermissionOptions().map(option => {
        const value = source[option.key];
        if (option.key === "memberInformation") return { ...option, props: { hasUnLimit: value ? 0 : 1 } };
        if (option.key === "manualService") return { ...option, props: { hasSupport: Number(value) || 0 } };
        if (option.key === "subAccountNum") {
            const supportNum = Number(value) || 0;
            return { ...option, props: { hasSupport: supportNum > 0 ? 1 : 0, supportNum: supportNum || undefined } };
        }
        if (value && typeof value === "object")
            return { ...option, props: { ...option.props, ...(value as Record<string, unknown>) } };
        return option;
    });
}

/** 按旧 Vue save() 规则组装会员权限接口对象。 */
function buildMemberPermission(options: MemberPermissionOption[]): Record<string, unknown> {
    const byKey = Object.fromEntries(options.map(option => [option.key, option.props]));
    const value = (key: string) => byKey[key] || {};
    const limited = (key: string, field: string) => {
        const props = value(key);
        return { hasUnLimit: props.hasUnLimit, [field]: props.hasUnLimit ? undefined : props[field] };
    };
    const support = (key: string, ...fields: string[]) => {
        const props = value(key);
        return {
            hasSupport: props.hasSupport,
            ...Object.fromEntries(fields.map(field => [field, props.hasSupport ? props[field] : undefined]))
        };
    };
    const member = value("memberInformation");
    const service = value("manualService");
    const subAccount = value("subAccountNum");
    return {
        productTypeIndexCheck: limited("productTypeIndexCheck", "visibleRange"),
        shippingPriceIndexCheck: limited("shippingPriceIndexCheck", "visibleRange"),
        shipQuery: limited("shipQuery", "times"),
        carQuery: limited("carQuery", "times"),
        memberInformation: member.hasUnLimit ? 0 : 1,
        intelligentAssistant: limited("intelligentAssistant", "times"),
        manualService: service.hasSupport,
        subAccountNum: subAccount.hasSupport ? subAccount.supportNum : 0,
        offlineBooth: support("offlineBooth", "boothNum", "joinNum", "quotaAmount"),
        mediaPromotion: {
            ...support("mediaPromotion", "freeNum"),
            freeNumUnit: value("mediaPromotion").freeNumUnit || 2
        }
    };
}

const numberValue = (value: unknown) => (value === "" || value == null ? undefined : Number(value));

/** 会员等级编辑页，保留旧 Vue 的费用、赠送和十类权限配置交互。 */

export function MemberLevelPage({
    loadLevels = loadMemberLevels,
    updateState = updateMemberLevelState,
    loadRegisterConfig = loadRegisterGiveConfig,
    saveRegisterConfig = saveRegisterGiveConfig
}: MemberLevelPageProps) {
    const navigate = useNavigate();
    const banButton = useUserStore(state => state.banButton);
    const [levels, setLevels] = useState<MemberLevelRecord[]>([]);
    const [errorMessage, setErrorMessage] = useState("");
    const [confirmState, setConfirmState] = useState<{ id: number; nextState: number; name: string }>();
    const [registerConfig, setRegisterConfig] = useState<RegisterGiveContent>({});
    const [savedRegisterConfig, setSavedRegisterConfig] = useState<RegisterGiveContent>({});
    const [registerConfigId, setRegisterConfigId] = useState(2);
    const [registerEditing, setRegisterEditing] = useState(false);

    const reload = async () => {
        try {
            setLevels(await loadLevels());
            setErrorMessage("");
        } catch {
            setLevels([]);
            setErrorMessage("会员等级加载失败");
        }
    };

    useEffect(() => {
        void reload();
        void loadRegisterConfig(2)
            .then(config => {
                const content = parseRegisterGiveContent(config.content);
                setRegisterConfigId(config.id || 2);
                setRegisterConfig(content);
                setSavedRegisterConfig(content);
            })
            .catch(() => undefined);
        // 首次加载只依赖外部 Vue API 适配器，避免每次状态更新重复请求。
    }, [loadLevels, loadRegisterConfig]);

    const openRegisterLevels = levels.filter(record => Number(record.state) === 1 && record.id > 1);
    const updateRegisterField = <K extends keyof RegisterGiveContent>(key: K, value: RegisterGiveContent[K]) =>
        setRegisterConfig(current => ({ ...current, [key]: value }));
    const saveRegister = async () => {
        const content = JSON.stringify(registerConfig);
        await saveRegisterConfig(registerConfigId, { type: 2, content });
        setSavedRegisterConfig(registerConfig);
        setRegisterEditing(false);
    };

    const openGrade = useMemo(
        () => levels.reduce((highest, record) => (isOpen(record) && record.id > highest ? record.id : highest), 1),
        [levels]
    );

    const handleState = (record: MemberLevelRecord) => {
        const nextState = isOpen(record) ? 0 : 1;
        if (record.id < openGrade) {
            setErrorMessage("无法关闭，请检查会员等级列表");
            return;
        }
        if (record.id > openGrade + 1) {
            setErrorMessage("无法开放，请检查会员等级列表");
            return;
        }
        setConfirmState({ id: record.id, nextState, name: record.name || "会员等级" });
    };

    const confirmStateChange = async () => {
        if (!confirmState) return;
        await updateState(confirmState.id, confirmState.nextState);
        setConfirmState(undefined);
        await reload();
    };

    return (
        <section aria-label="member-level-page" className="flex h-full min-h-full flex-col">
            <Card>
                <Typography.Title className="mb-5 mt-0" level={4}>
                    等级配置
                </Typography.Title>
                {errorMessage ? <Alert className="mb-4" message={errorMessage} role="alert" type="error" /> : null}
                <div className="grid gap-4 md:grid-cols-3">
                    {levels.map(record => (
                        <Card key={record.id} bordered className="min-h-44" title={record.name || DEFAULT_VALUE}>
                            {record.id > 1 ? (
                                <Typography.Text>费用：{formatFee(record)}</Typography.Text>
                            ) : (
                                <Typography.Text>免费会员</Typography.Text>
                            )}
                            {record.giftFlag ? (
                                <Typography.Paragraph>赠送：{record.giftDuration || 0}天</Typography.Paragraph>
                            ) : null}
                            <Space>
                                <Button
                                    aria-label={`修改${record.name || "会员"}`}
                                    disabled={banButton}
                                    onClick={() => navigate(`/lianYunUser/memberLevelinfo?id=${record.id}`)}
                                >
                                    修改
                                </Button>
                                {record.id > 1 ? (
                                    <Button
                                        aria-label={`${isOpen(record) ? "关闭" : "开放"}${record.name || "会员"}`}
                                        disabled={banButton}
                                        onClick={() => handleState(record)}
                                    >
                                        {isOpen(record) ? "关闭" : "开放"}
                                    </Button>
                                ) : null}
                            </Space>
                        </Card>
                    ))}
                </div>
            </Card>
            <Card className="mt-6">
                <Typography.Title className="mb-5 mt-0" level={4}>
                    注册送会员配置
                </Typography.Title>
                {registerEditing ? (
                    <div className="flex flex-wrap gap-4">
                        <label>
                            是否开启
                            <select
                                aria-label="是否开启注册送会员"
                                value={String(registerConfig.hasOpen ?? 0)}
                                onChange={event => updateRegisterField("hasOpen", Number(event.target.value))}
                            >
                                <option value="1">是</option>
                                <option value="0">否</option>
                            </select>
                        </label>
                        <label>
                            赠送会员类型
                            <select
                                aria-label="赠送会员类型"
                                value={String(registerConfig.level ?? "")}
                                onChange={event => updateRegisterField("level", Number(event.target.value))}
                            >
                                <option value="">请选择</option>
                                {openRegisterLevels.map(record => (
                                    <option key={record.id} value={record.id}>
                                        {record.name}
                                    </option>
                                ))}
                            </select>
                        </label>
                        <label>
                            赠送时长
                            <Input
                                aria-label="注册送会员时长"
                                type="number"
                                value={registerConfig.duration ?? ""}
                                onChange={event => updateRegisterField("duration", numberValue(event.target.value))}
                            />
                        </label>
                        <label>
                            兑换有效期
                            <Input
                                aria-label="注册送会员兑换有效期"
                                type="number"
                                value={registerConfig.effectiveDuration ?? ""}
                                onChange={event =>
                                    updateRegisterField("effectiveDuration", numberValue(event.target.value))
                                }
                            />
                        </label>
                    </div>
                ) : (
                    <Space direction="vertical">
                        <span>
                            赠送会员类型：
                            {openRegisterLevels.find(record => record.id === registerConfig.level)?.name ||
                                DEFAULT_VALUE}
                        </span>
                        <span>
                            赠送时长：{registerConfig.duration == null ? DEFAULT_VALUE : `${registerConfig.duration}天`}
                        </span>
                        <span>
                            兑换有效期：
                            {registerConfig.effectiveDuration == null
                                ? DEFAULT_VALUE
                                : `${registerConfig.effectiveDuration}天`}
                        </span>
                    </Space>
                )}
                <Space className="mt-4">
                    {registerEditing ? (
                        <>
                            <Button aria-label="保存注册送会员配置" type="primary" onClick={() => void saveRegister()}>
                                保存
                            </Button>
                            <Button
                                onClick={() => {
                                    setRegisterConfig(savedRegisterConfig);
                                    setRegisterEditing(false);
                                }}
                            >
                                取消
                            </Button>
                        </>
                    ) : (
                        <Button aria-label="修改注册送会员配置" onClick={() => setRegisterEditing(true)}>
                            修改
                        </Button>
                    )}
                </Space>
            </Card>
            <Modal
                open={Boolean(confirmState)}
                title={confirmState?.nextState ? "是否开放" : "是否关闭"}
                onCancel={() => setConfirmState(undefined)}
                onOk={() => void confirmStateChange()}
                okText="确认"
                cancelText="取消"
                okButtonProps={{ "aria-label": "确认" }}
                cancelButtonProps={{ "aria-label": "取消" }}
            >
                {confirmState?.nextState
                    ? `开放后，用户将可以购买或兑换${confirmState.name}`
                    : `关闭后，用户将不能购买或兑换${confirmState?.name}`}
            </Modal>
        </section>
    );
}

export function MemberLevelFormPage({
    memberId,
    loadDetail = loadMemberLevelDetail,
    updateMember = updateMemberLevel
}: MemberLevelFormPageProps) {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const banButton = useUserStore(state => state.banButton);
    const resolvedMemberId = memberId ?? (Number(searchParams.get("id") || 0) || undefined);
    const [form, setForm] = useState<MemberLevelRecord>({
        id: resolvedMemberId || 0,
        name: "",
        yearDiscountFlag: 0,
        giftFlag: 0
    });
    const [permissionOptions, setPermissionOptions] = useState<MemberPermissionOption[]>(createPermissionOptions);
    const [errorMessage, setErrorMessage] = useState("");

    useEffect(() => {
        if (resolvedMemberId == null) return;
        void loadDetail(resolvedMemberId)
            .then(detail => {
                setForm(detail);
                setPermissionOptions(hydratePermissionOptions(detail.memberPermission || detail.feature));
            })
            .catch(() => setErrorMessage("会员等级详情加载失败"));
    }, [loadDetail, resolvedMemberId]);

    const updateField = <K extends keyof MemberLevelRecord>(key: K, value: MemberLevelRecord[K]) =>
        setForm(current => ({ ...current, [key]: value }));

    /** 更新单条权限行，保持 React 状态不可变并兼容 Vue props 结构。 */
    const updatePermission = (index: number, patch: Record<string, unknown>) => {
        setPermissionOptions(current =>
            current.map((option, optionIndex) =>
                optionIndex === index ? { ...option, props: { ...option.props, ...patch } } : option
            )
        );
    };

    /** 数值权限沿用 Vue 的大于 0 且小于 10000 校验。 */
    const validatePermissions = () => {
        const invalid = permissionOptions.some(option => {
            const props = option.props;
            const fields =
                option.layout === 2 && !props.hasUnLimit
                    ? ["times"]
                    : option.layout === 5 && props.hasSupport
                      ? ["supportNum"]
                      : option.layout === 6 && props.hasSupport
                        ? ["boothNum", "joinNum", "quotaAmount"]
                        : option.layout === 7 && props.hasSupport
                          ? ["freeNum"]
                          : [];
            return fields.some(field => {
                const value = Number(props[field]);
                return !Number.isFinite(value) || value < 1 || value >= 10000;
            });
        });
        if (invalid) setErrorMessage("权限数值需大于0且小于10000");
        return !invalid;
    };

    /** 年费、折扣和赠送时长校验，保留旧 Vue 表单边界。 */
    const validateFee = () => {
        if (form.id === 1) return true;
        const yearFee = Number(form.yearFee);
        if (!Number.isFinite(yearFee) || yearFee < 0.01 || yearFee > 999999.99) {
            setErrorMessage("年度费用需大于0且小于1000000");
            return false;
        }
        if (Number(form.yearDiscountFlag) === ANNUAL_MEMBER_STATE.DISCOUNT.code) {
            const discountFee = Number(form.yearDiscountFee);
            if (!Number.isFinite(discountFee) || discountFee < 0.01 || discountFee >= yearFee) {
                setErrorMessage("折扣价必须小于原价");
                return false;
            }
        }
        if (Number(form.giftFlag) === GIFT_MEMBER_STATE.GIFT.code) {
            const duration = Number(form.giftDuration);
            if (!Number.isFinite(duration) || duration < 1 || duration >= 1000000) {
                setErrorMessage("赠送时长需大于0且小于1000000");
                return false;
            }
        }
        return true;
    };

    const submit = async () => {
        setErrorMessage("");
        if (!validateFee() || !validatePermissions()) return;
        try {
            const memberPermission = buildMemberPermission(permissionOptions);
            await updateMember({
                ...form,
                yearDiscountFee:
                    Number(form.yearDiscountFlag) === ANNUAL_MEMBER_STATE.DISCOUNT.code
                        ? form.yearDiscountFee
                        : undefined,
                memberPermission
            });
            navigate(-1);
        } catch {
            setErrorMessage("会员等级保存失败");
        }
    };

    const renderPermissionControl = (option: MemberPermissionOption, index: number) => {
        const props = option.props;
        const setNumber = (field: string, value: string) => updatePermission(index, { [field]: numberValue(value) });
        if (option.layout === 1) {
            const visibleRange = Array.isArray(props.visibleRange) ? (props.visibleRange as number[]) : [1];
            return (
                <>
                    <label>
                        <input
                            type="radio"
                            checked={props.hasUnLimit === 1}
                            onChange={() => updatePermission(index, { hasUnLimit: 1 })}
                        />{" "}
                        无限制
                    </label>
                    <label>
                        <input
                            type="radio"
                            checked={props.hasUnLimit === 0}
                            onChange={() => updatePermission(index, { hasUnLimit: 0 })}
                        />{" "}
                        限制
                    </label>
                    {!props.hasUnLimit ? (
                        <span className="ml-4">
                            {VISIBLE_CONTENT_STATE.options.map(item => (
                                <label key={String(item.value)} className="mr-3">
                                    <input
                                        type="checkbox"
                                        checked={visibleRange.includes(Number(item.value))}
                                        disabled={Number(item.value) === 1}
                                        onChange={event => {
                                            const next = event.target.checked
                                                ? [...visibleRange, Number(item.value)]
                                                : visibleRange.filter(value => value !== Number(item.value));
                                            updatePermission(index, { visibleRange: [...new Set(next)].sort() });
                                        }}
                                    />
                                    {item.label}
                                </label>
                            ))}
                        </span>
                    ) : null}
                </>
            );
        }
        if (option.layout === 2) {
            return (
                <>
                    <label>
                        <input
                            type="radio"
                            checked={props.hasUnLimit === 1}
                            onChange={() => updatePermission(index, { hasUnLimit: 1 })}
                        />{" "}
                        无限制
                    </label>
                    <label>
                        <input
                            type="radio"
                            checked={props.hasUnLimit === 0}
                            onChange={() => updatePermission(index, { hasUnLimit: 0 })}
                        />{" "}
                        限制
                    </label>
                    {!props.hasUnLimit ? (
                        <Input
                            aria-label={`${option.label}-限制次数`}
                            type="number"
                            placeholder="请输入次数"
                            value={(props.times as number | undefined) ?? ""}
                            onChange={event => setNumber("times", event.target.value)}
                        />
                    ) : null}
                </>
            );
        }
        if (option.layout === 3) {
            return (
                <>
                    <label>
                        <input
                            type="radio"
                            checked={props.hasUnLimit === 1}
                            onChange={() => updatePermission(index, { hasUnLimit: 1 })}
                        />{" "}
                        无限制
                    </label>
                    <label>
                        <input
                            type="radio"
                            checked={props.hasUnLimit === 0}
                            onChange={() => updatePermission(index, { hasUnLimit: 0 })}
                        />{" "}
                        限制
                    </label>
                </>
            );
        }
        if (option.layout === 4) {
            return (
                <>
                    <label>
                        <input
                            type="radio"
                            checked={props.hasSupport === 1}
                            onChange={() => updatePermission(index, { hasSupport: 1 })}
                        />{" "}
                        支持
                    </label>
                    <label>
                        <input
                            type="radio"
                            checked={props.hasSupport === 0}
                            onChange={() => updatePermission(index, { hasSupport: 0 })}
                        />{" "}
                        不支持
                    </label>
                </>
            );
        }
        const supportFields =
            option.layout === 5
                ? [["supportNum", "支持数量", "个"]]
                : option.layout === 6
                  ? [
                        ["boothNum", "免费提供展位数量", "个/场"],
                        ["joinNum", "参与人数", "人/场"],
                        ["quotaAmount", "名额价值", "元"]
                    ]
                  : [["freeNum", "免费推广次数", "条"]];
        return (
            <>
                <label>
                    <input
                        type="radio"
                        checked={props.hasSupport === 1}
                        onChange={() => updatePermission(index, { hasSupport: 1 })}
                    />{" "}
                    支持
                </label>
                <label>
                    <input
                        type="radio"
                        checked={props.hasSupport === 0}
                        onChange={() => updatePermission(index, { hasSupport: 0 })}
                    />{" "}
                    不支持
                </label>
                {props.hasSupport
                    ? supportFields.map(([field, label, suffix]) => (
                          <label key={field} className="ml-4">
                              {label}
                              <Input
                                  aria-label={`${option.label}-${label}`}
                                  type="number"
                                  value={(props[field] as number | undefined) ?? ""}
                                  onChange={event => setNumber(field, event.target.value)}
                              />{" "}
                              {suffix}
                          </label>
                      ))
                    : null}
                {option.layout === 7 && props.hasSupport ? (
                    <select
                        aria-label="免费推广次数单位"
                        value={String(props.freeNumUnit || 2)}
                        onChange={event => updatePermission(index, { freeNumUnit: Number(event.target.value) })}
                    >
                        {FREE_NUM_UNIT.options.map(item => (
                            <option key={String(item.value)} value={String(item.value)}>
                                {item.label}
                            </option>
                        ))}
                    </select>
                ) : null}
            </>
        );
    };

    return (
        <section aria-label="member-level-form-page" className="flex h-full min-h-full flex-col">
            <Card>
                <Typography.Title className="mb-5 mt-0" level={4}>
                    {form.name || "权限设置"}
                </Typography.Title>
                {errorMessage ? <Alert className="mb-4" message={errorMessage} role="alert" type="error" /> : null}
                {form.id !== 1 ? (
                    <ZhGrid cols={2} gap={[32, 20]}>
                        <ZhGridItem>
                            <label>
                                年度费用
                                <Input
                                    aria-label="年度费用"
                                    type="number"
                                    value={form.yearFee ?? ""}
                                    onChange={event => updateField("yearFee", numberValue(event.target.value))}
                                />
                            </label>
                        </ZhGridItem>
                        <ZhGridItem>
                            <label>
                                年度费用优惠
                                <label>
                                    <input
                                        type="radio"
                                        checked={Number(form.yearDiscountFlag) === 0}
                                        onChange={() => updateField("yearDiscountFlag", 0)}
                                    />
                                    不优惠
                                </label>
                                <label>
                                    <input
                                        type="radio"
                                        checked={Number(form.yearDiscountFlag) === 1}
                                        onChange={() => updateField("yearDiscountFlag", 1)}
                                    />
                                    优惠
                                </label>
                            </label>
                        </ZhGridItem>
                        {Number(form.yearDiscountFlag) === 1 ? (
                            <ZhGridItem>
                                <label>
                                    折扣价
                                    <Input
                                        aria-label="年度折扣价"
                                        type="number"
                                        value={form.yearDiscountFee ?? ""}
                                        onChange={event =>
                                            updateField("yearDiscountFee", numberValue(event.target.value))
                                        }
                                    />
                                </label>
                            </ZhGridItem>
                        ) : null}
                        <ZhGridItem>
                            <label>
                                赠送会员
                                <label>
                                    <input
                                        type="radio"
                                        checked={Number(form.giftFlag) === 0}
                                        onChange={() => updateField("giftFlag", 0)}
                                    />
                                    不赠送
                                </label>
                                <label>
                                    <input
                                        type="radio"
                                        checked={Number(form.giftFlag) === 1}
                                        onChange={() => updateField("giftFlag", 1)}
                                    />
                                    赠送
                                </label>
                            </label>
                        </ZhGridItem>
                        {Number(form.giftFlag) === 1 ? (
                            <ZhGridItem>
                                <label>
                                    赠送时长
                                    <Input
                                        aria-label="赠送时长"
                                        type="number"
                                        value={form.giftDuration ?? ""}
                                        onChange={event => updateField("giftDuration", numberValue(event.target.value))}
                                    />
                                    <select
                                        aria-label="赠送时长单位"
                                        value={String(form.giftDurationUnit || GIFT_DURATION_UNIT.DAY.code)}
                                        onChange={event => updateField("giftDurationUnit", Number(event.target.value))}
                                    >
                                        {GIFT_DURATION_UNIT.options.map(item => (
                                            <option key={String(item.value)} value={String(item.value)}>
                                                {item.label}
                                            </option>
                                        ))}
                                    </select>
                                </label>
                            </ZhGridItem>
                        ) : null}
                    </ZhGrid>
                ) : null}
                <Typography.Title className="mb-4 mt-6" level={5}>
                    配置权限
                </Typography.Title>
                <ZhGrid cols={1} gap={[16, 16]}>
                    {permissionOptions.map((option, index) => (
                        <ZhGridItem key={option.key}>
                            <div className="flex flex-wrap items-center gap-3">
                                <Typography.Text strong>{option.label}</Typography.Text>
                                {renderPermissionControl(option, index)}
                            </div>
                        </ZhGridItem>
                    ))}
                </ZhGrid>
                <Space className="mt-6">
                    <Button type="primary" aria-label="保存会员配置" disabled={banButton} onClick={() => void submit()}>
                        保存
                    </Button>
                    <Button onClick={() => navigate(-1)}>返回</Button>
                </Space>
            </Card>
        </section>
    );
}
